import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CustomTable } from 'components/molecules/GrowthAnalysis/GrowthAnalysisTable';
import PlusSvgComponent from 'assets/svg/Plus';
import AddKPIModal from 'components/organism/PriceSimulator/Modals/AddKPIModal';
import { KpiMetricType, priceSimulatorDataContext } from 'contexts/PriceSimulatorContext';
import ConfirmEventModal from 'components/organism/PromoRecommendation/ConfirmationModal';
import { getGoalsKpiMetricsColumns } from 'utils/getPriceSimulatorColumns';
import { getConfig, getPercentageChangeV2, scaleMaxValue } from 'utils/helperMethods';
import _ from 'lodash';
import { FilterContextData } from 'contexts/FilterContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PSR_ADD_MORE_KPI_CLICKED,
    PSR_KPI_GOALS_INPUT_VALUE_CHANGED,
    PSR_KPI_GOALS_PERCENTAGE_VALUE_CHANGED,
} from 'utils/mixpanelEventNames';
import { UserContext } from 'contexts/ConfigContext';

const KPIAddTable = () => {
    const [modalVisible, setModalVisible] = useState(false);
    const toDeleteKPIkey = useRef<any>(null);
    const [showDeletePopUp, setShowDeletePopUp] = useState(false);
    const {
        viewMode,
        selectedKpiValues,
        setSelectedKpiValues,
        kpiMetrictsState,
        setKpiMetrictsState,
        kpiMetricsLoading,
        createSimulatorRef,
        simulationDateRange,
    } = useContext(priceSimulatorDataContext);
    const { allDimAndAttributeTypeList, idForRestOfComponentLoading } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const simulatorConfig = getConfig('priceSimulator', appConfig);
    const [kpiTableHeight, setKpiTableHeight] = useState(400);

    useEffect(() => {
        if (createSimulatorRef.current) {
            const rect = createSimulatorRef.current?.getBoundingClientRect();
            const tableHeight = (rect?.height ?? 700) - 700;
            setKpiTableHeight(tableHeight);
        }
    }, []);

    useEffect(() => {
        let newData: any[] = [];
        kpiMetrictsState.forEach((item: any) => {
            const isSelected = selectedKpiValues.includes(Number(item.key));
            const newItem = {
                ...item,
                selected: isSelected,
                goalValue: isSelected ? item?.goalValue : '',
                percentChange: isSelected ? item?.percentChange : '',
            };
            newData.push(newItem);
        });

        setKpiMetrictsState(newData);
    }, [selectedKpiValues]);

    const showModal = () => {
        setModalVisible(true);
        trackEventMixpanel(PSR_ADD_MORE_KPI_CLICKED);
    };

    const closeModal = () => {
        setModalVisible(false);
    };

    const onDeleteClick = (key: string) => {
        toDeleteKPIkey.current = key;
        setShowDeletePopUp(true);
    };

    const onConfirmDeleteHandler = () => {
        const newselectedKpiValues = selectedKpiValues
            .filter((item: number) => item !== toDeleteKPIkey.current)
            .map((item: any) => Number(item));
        setSelectedKpiValues(newselectedKpiValues);
        setShowDeletePopUp(false);
    };

    const handleInputValuesChanges = (value: any, key: any, isGoalValue: boolean) => {
        if (isNaN(value)) return;
        const newData = [...kpiMetrictsState];
        const target = newData.find((item) => key === item.key);
        if (target) {
            target.goalValue = isGoalValue
                ? value
                : value === ''
                ? ''
                : _.round(scaleMaxValue(value, Number(target.actualValue) || 0));
            target.percentChange = !isGoalValue
                ? value
                : value === ''
                ? ''
                : _.round(getPercentageChangeV2(Number(target.actualValue) || 0, value));
        }
        if (isGoalValue) {
            trackEventMixpanel(PSR_KPI_GOALS_INPUT_VALUE_CHANGED, {
                kpi: target?.name,
                value,
            });
        } else {
            trackEventMixpanel(PSR_KPI_GOALS_PERCENTAGE_VALUE_CHANGED, {
                kpi: target?.name,
                value,
            });
        }
        setKpiMetrictsState(newData);
    };

    const kpiMetricsColumns = useMemo(() => {
        return getGoalsKpiMetricsColumns(
            viewMode || simulationDateRange?.includes(null),
            handleInputValuesChanges,
            onDeleteClick,
            simulationDateRange,
            simulatorConfig?.kpiMetrics || [],
        );
    }, [kpiMetrictsState]);

    const commonLoading = kpiMetricsLoading;
    return (
        <>
            <div className="font-medium font-md my-2  "> Key Performance Indices (KPI)</div>
            <CustomTable
                dataSource={kpiMetrictsState.filter((item: KpiMetricType) => item.selected)}
                columns={kpiMetricsColumns as any}
                bordered={false}
                pagination={false}
                scroll={{ x: '100%', y: 250 }}
                sticky
                loading={commonLoading}
                id="price-sim-kpi-table"
            />
            <div>
                <button
                    type="button"
                    onClick={() => !viewMode && showModal()}
                    className={`rounded mt-2 px-8 py-2 border font-semibold cursor-pointer border-purpleFont  bg-purpleFont text-white flex items-center justify-center mb-6 ${
                        viewMode ? 'cursor-not-allowed opacity-80' : ''
                    }`}
                    style={{ marginBottom: '80px' }}
                    id="price-sim-add-more-kpi"
                >
                    <PlusSvgComponent fill="white" /> Add More KPI
                </button>
                <AddKPIModal visible={modalVisible} closeModal={closeModal} kpiMetrics={kpiMetrictsState} />
            </div>
            <ConfirmEventModal
                onYes={onConfirmDeleteHandler}
                setShowModal={setShowDeletePopUp}
                showModal={showDeletePopUp}
                confirmationTextFromParent={`${
                    kpiMetrictsState.find((item: KpiMetricType) => item.key === toDeleteKPIkey.current)?.name
                } will be removed from Key performance table.`}
            />
        </>
    );
};

export default KPIAddTable;
